import { useEffect, useRef, useState } from 'react'
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  MenuItem,
  TextField
} from '@mui/material'
import ModalImagenBarbeibot from '../../modals/ModalImagenBarbeibot/ModalImagenBarbeibot'
import css from './BarbeibotQuestionWithAttachment.module.css'

const defaultModelOptions = [
  { value: 'gpt-5.4', label: 'GPT-5.4' },
  { value: 'gpt-5.4-mini', label: 'GPT-5.4 Mini' },
  { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
  { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' },
  { value: 'claude-opus-4-6', label: 'Claude Opus 4.6' },
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6' }
]

const BarbeibotQuestionWithAttachment = ({
  title = 'Haz una pregunta y adjunta un archivo o imagen',
  subtitle,
  onSubmit,
  isLoading = false,
  accept = 'image/*,.pdf,.doc,.docx,.txt',
  modelOptions = defaultModelOptions,
  maxListenSeconds = 60
}) => {
  const [question, setQuestion] = useState('')
  const [model, setModel] = useState(modelOptions[0]?.value || '')
  const [files, setFiles] = useState([])
  const [previewUrls, setPreviewUrls] = useState([])
  const [isDragging, setIsDragging] = useState(false)
  const [showImage, setShowImage] = useState(false)

  const [isListening, setIsListening] = useState(false)
  const recognitionRef = useRef(null)
  const listenTimeoutRef = useRef(null)

  const fileInputRef = useRef(null)
  const previewUrlsRef = useRef([])
  previewUrlsRef.current = previewUrls

  useEffect(() => {
    return () => {
      previewUrlsRef.current.forEach(url => url && URL.revokeObjectURL(url))
    }
  }, [])

  useEffect(() => {
    return () => {
      clearTimeout(listenTimeoutRef.current)
      if (recognitionRef.current) {
        recognitionRef.current.onresult = null
        recognitionRef.current.onerror = null
        recognitionRef.current.onend = null
        recognitionRef.current.stop()
      }
    }
  }, [])

  useEffect(() => {
    if (!modelOptions.length) {
      setModel('')
      return
    }

    setModel(prev => {
      const hasSelectedModel = modelOptions.some(
        option => option.value === prev
      )
      return hasSelectedModel ? prev : modelOptions[0].value
    })
  }, [modelOptions])

  const handleFiles = selectedFiles => {
    if (!selectedFiles || selectedFiles.length === 0) return
    const newFiles = Array.from(selectedFiles)
    const newPreviewUrls = newFiles.map(file =>
      file.type.startsWith('image/') ? URL.createObjectURL(file) : null
    )
    setFiles(prev => [...prev, ...newFiles])
    setPreviewUrls(prev => [...prev, ...newPreviewUrls])
  }

  const handleInputFile = e => handleFiles(e.target.files)

  const handleDrop = e => {
    e.preventDefault()
    setIsDragging(false)
    handleFiles(e.dataTransfer.files)
  }

  const handleDragOver = e => {
    e.preventDefault()
    setIsDragging(true)
  }

  const handleDragLeave = e => {
    e.preventDefault()
    if (e.currentTarget.contains(e.relatedTarget)) return
    setIsDragging(false)
  }

  const handlePickFile = () => {
    fileInputRef.current?.click()
  }

  const handleRemoveFile = index => {
    const newFiles = files.filter((_, i) => i !== index)
    const newPreviewUrls = previewUrls.filter((_, i) => i !== index)

    if (previewUrls[index]) {
      URL.revokeObjectURL(previewUrls[index])
    }

    setFiles(newFiles)
    setPreviewUrls(newPreviewUrls)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleClearFiles = () => {
    previewUrls.forEach(url => URL.revokeObjectURL(url))
    setFiles([])
    setPreviewUrls([])
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const isSpeechSupported =
    typeof window !== 'undefined' &&
    !!(window.SpeechRecognition || window.webkitSpeechRecognition)

  const stopListening = () => {
    clearTimeout(listenTimeoutRef.current)
    recognitionRef.current?.stop()
    setIsListening(false)
  }

  const handleToggleMic = () => {
    if (!isSpeechSupported) return

    if (isListening) {
      stopListening()
      return
    }

    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition
    const recognition = new SpeechRecognition()
    recognition.lang = 'es-ES'
    recognition.continuous = true
    recognition.interimResults = false
    recognition.maxAlternatives = 1

    recognition.onresult = e => {
      for (let i = e.resultIndex; i < e.results.length; i++) {
        if (e.results[i].isFinal) {
          const texto = e.results[i][0].transcript
          setQuestion(prev => (prev ? prev + ' ' + texto : texto))
        }
      }
    }

    recognition.onerror = () => stopListening()
    recognition.onend = () => setIsListening(false)

    recognitionRef.current = recognition
    recognition.start()
    setIsListening(true)

    listenTimeoutRef.current = setTimeout(() => {
      stopListening()
    }, maxListenSeconds * 1000)
  }

  const handleSubmit = e => {
    e.preventDefault()

    const trimmedQuestion = question.trim()
    if (!trimmedQuestion) return

    onSubmit({ question: trimmedQuestion, files, model })
    setQuestion('')
    handleClearFiles()
  }

  const isSubmitDisabled = isLoading || !question.trim()

  return (
    <form
      onSubmit={handleSubmit}
      className={css.form}
      data-drop={isDragging ? 'active' : 'inactive'}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      <Card variant='outlined' className={css.card}>
        <CardHeader
          title={title}
          subheader={subtitle ? `💡 ${subtitle}` : undefined}
          titleTypographyProps={{ variant: 'h6' }}
        />
        <CardContent className={css.content}>
          <input
            type='file'
            ref={fileInputRef}
            accept={accept}
            className={css.fileInput}
            onChange={handleInputFile}
            multiple
          />

          <TextField
            fullWidth
            multiline
            minRows={8}
            maxRows={20}
            variant='outlined'
            placeholder='Escribe tu pregunta...'
            value={question}
            onChange={e => setQuestion(e.target.value)}
            disabled={isLoading}
          />

          {files.length > 0 && (
            <div className={css.filesList}>
              {files.map((file, index) => (
                <div key={index} className={css.fileChip}>
                  {previewUrls[index] ? (
                    <img
                      src={previewUrls[index]}
                      alt={`Vista previa ${file.name}`}
                      className={css.preview}
                      onClick={() => setShowImage(index)}
                    />
                  ) : (
                    <i className={`material-icons ${css.fileIcon}`}>
                      description
                    </i>
                  )}
                  <span className={css.fileName} title={file.name}>
                    {file.name}
                  </span>
                  <button
                    type='button'
                    className={css.removeButton}
                    onClick={() => handleRemoveFile(index)}
                    aria-label={`Quitar ${file.name}`}
                    title='Quitar'
                    disabled={isLoading}
                  >
                    <i className='material-icons'>delete_outline</i>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className={css.actionsBar}>
            <div className={css.leftActions}>
              <TextField
                select
                label='Modelo'
                value={model}
                onChange={e => setModel(e.target.value)}
                size='small'
                disabled={isLoading || !modelOptions.length}
                className={css.modelSelect}
              >
                {modelOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <div className={css.iconActions}>
                <button
                  type='button'
                  onClick={handlePickFile}
                  disabled={isLoading}
                  className={css.iconButton}
                  title='Adjuntar archivo'
                  aria-label='Adjuntar archivo'
                >
                  <i className='material-icons'>attach_file</i>
                </button>

                {isSpeechSupported && (
                  <button
                    type='button'
                    onClick={handleToggleMic}
                    disabled={isLoading}
                    className={`${css.iconButton} ${isListening ? css.micActive : ''}`}
                    title={isListening ? 'Detener grabación' : 'Hablar'}
                    aria-label={isListening ? 'Detener grabación' : 'Hablar'}
                  >
                    <i className='material-icons'>
                      {isListening ? 'fiber_manual_record' : 'keyboard_voice'}
                    </i>
                  </button>
                )}
              </div>
            </div>

            <Button
              type='submit'
              variant='contained'
              color='primary'
              disabled={isSubmitDisabled}
              className={css.submitButton}
            >
              Enviar
            </Button>
          </div>
        </CardContent>
      </Card>

      <ModalImagenBarbeibot
        open={showImage !== false && !!previewUrls[showImage]}
        onClose={() => setShowImage(false)}
        onDelete={() => {
          handleRemoveFile(showImage)
          setShowImage(false)
        }}
        url={previewUrls[showImage]}
      />
    </form>
  )
}

export default BarbeibotQuestionWithAttachment
